/** Shared exact-evidence chart used by Overview and detailed Analytics. */
import type { EChartsCoreOption } from "echarts/core";
import { Button } from "@/design-system/Button";
import { useEffect, useRef, useState } from "react";

import { Panel } from "@/design-system/Panel";
import type {
  AccountAnalyticsResponse,
  AnalyticsPointResponse,
} from "@/shared/api";
import { finiteGeometryValue } from "@/shared/money/geometry";

type SeriesId = "portfolio" | "realised";
type VisibleSeries = SeriesId | "both";

export type ChartPalette = {
  readonly background: string;
  readonly border: string;
  readonly grid: string;
  readonly fontMono: string;
  readonly fontSans: string;
  readonly negative: string;
  readonly positive: string;
  readonly realised: string;
  readonly text: string;
  readonly muted: string;
};

type ExactSeries = {
  readonly portfolio: readonly AnalyticsPointResponse[];
  readonly realised: readonly AnalyticsPointResponse[];
};

type ChartHandle = {
  readonly setOption: (option: EChartsCoreOption, notMerge?: boolean) => void;
  readonly resize: () => void;
  readonly dispose: () => void;
};

type ChartState = {
  readonly analytics: AccountAnalyticsResponse;
  readonly portfolioAvailable: boolean;
  readonly visible: VisibleSeries;
};

const SERIES = {
  portfolio: "Общий PnL портфеля",
  realised: "Накопленный реализованный PnL (NET)",
} as const;

const DECIMAL = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;
const AMOUNT = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 });
const MOMENT = new Intl.DateTimeFormat("ru-RU", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "UTC",
});

function chartLabel(): string {
  return "Общий PnL портфеля и накопленный реализованный PnL (NET), общая шкала USDT";
}

function exactPoints(
  points: readonly AnalyticsPointResponse[],
): readonly AnalyticsPointResponse[] {
  return points.filter(
    (point) => Number.isFinite(Date.parse(point.at)) && DECIMAL.test(point.value),
  );
}

function exactSeries(
  analytics: AccountAnalyticsResponse,
  portfolioAvailable: boolean,
): ExactSeries {
  return {
    portfolio: portfolioAvailable ? exactPoints(analytics.portfolio) : [],
    realised: exactPoints(analytics.realised),
  };
}


function geometry(points: readonly AnalyticsPointResponse[]): readonly [number, number][] {
  return points.flatMap((point) => {
    const instant = Date.parse(point.at);
    const value = finiteGeometryValue(point.value);
    return Number.isFinite(instant) && value !== null ? [[instant, value]] : [];
  });
}

function formatAxisMoment(value: number): string {
  return MOMENT.format(new Date(value)).replace(".", "");
}

function lastValue(values: readonly unknown[]): unknown {
  return values.at(-1);
}

function formatAmount(value: unknown): string {
  const numeric: unknown = Array.isArray(value) ? lastValue(value) : value;
  return typeof numeric === "number" && Number.isFinite(numeric)
    ? `${AMOUNT.format(numeric)} USDT`
    : "—";
}

export function buildPnlChartOption(
  analytics: AccountAnalyticsResponse,
  portfolioAvailable: boolean,
  palette: ChartPalette,
  visible: VisibleSeries = "both",
): EChartsCoreOption {
  const exact = exactSeries(analytics, portfolioAvailable);
  const portfolio = visible === "realised" ? [] : geometry(exact.portfolio);
  const realised = visible === "portfolio" ? [] : geometry(exact.realised);
  const portfolioEnd = portfolio.at(-1)?.[1] ?? 0;
  const portfolioColor =
    portfolioEnd < 0
      ? palette.negative
      : portfolioEnd > 0
        ? palette.positive
        : palette.muted;
  const firstInstant = Math.min(
    ...[...portfolio, ...realised].map(([instant]) => instant),
  );
  const coverageInstant =
    analytics.coverage_since === null ? Number.NaN : Date.parse(analytics.coverage_since);
  const missingCoverage =
    Number.isFinite(firstInstant) &&
    Number.isFinite(coverageInstant) &&
    firstInstant < coverageInstant;

  return {
    animation: false,
    aria: {
      enabled: true,
      decal: { show: true },
      description: chartLabel(),
    },
    backgroundColor: "transparent",
    // Room above the plot for the axis name. At sixteen the "USDT" sitting
    // over the right-hand axis was cut in half by the top of the panel.
    grid: { left: 12, right: 24, top: 28, bottom: 38, containLabel: true },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross" },
      backgroundColor: palette.background,
      borderColor: palette.border,
      borderWidth: 1,
      textStyle: { color: palette.text, fontFamily: palette.fontSans },
      valueFormatter: formatAmount,
    },
    axisPointer: { link: [{ xAxisIndex: "all" }] },
    dataZoom: [
      {
        type: "inside",
        xAxisIndex: 0,
        filterMode: "none",
        moveOnMouseWheel: false,
        zoomOnMouseWheel: "ctrl",
      },
    ],
    xAxis: {
      type: "time",
      boundaryGap: false,
      axisLine: { lineStyle: { color: palette.border } },
      axisTick: { show: false },
      axisLabel: {
        color: palette.muted,
        fontFamily: palette.fontMono,
        hideOverlap: true,
        formatter: formatAxisMoment,
      },
      splitLine: { show: false },
      axisPointer: {
        label: {
          formatter: ({ value }: { value: number }) => formatAxisMoment(value),
        },
      },
    },
    yAxis: {
      type: "value",
      name: "USDT",
      position: "right",
      scale: true,
      nameTextStyle: { color: palette.muted, fontFamily: palette.fontMono },
      axisLabel: {
        color: palette.muted,
        fontFamily: palette.fontMono,
        formatter: (value: number) => AMOUNT.format(value),
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitNumber: 4,
      splitLine: { lineStyle: { color: palette.grid, type: "dashed" } },
    },
    series: [
      {
        name: SERIES.portfolio,
        type: "line",
        data: portfolio,
        showSymbol: false,
        symbol: "circle",
        smooth: false,
        connectNulls: false,
        itemStyle: { color: portfolioColor },
        lineStyle: { color: portfolioColor, width: 2.5 },
        // Filled enough to be a shape rather than a tint. At 0.09 the area was
        // a smudge behind the line and the chart still read as a hairline.
        areaStyle: { color: portfolioColor, opacity: 0.24, origin: "auto" },
        emphasis: { focus: "series", scale: 1.5 },
        markLine: {
          silent: true,
          symbol: "none",
          label: { show: false },
          lineStyle: { color: palette.border, width: 1.25 },
          data: [{ yAxis: 0 }],
        },
      },
      {
        name: SERIES.realised,
        type: "line",
        data: realised,
        showSymbol: false,
        symbol: "circle",
        smooth: false,
        step: "end",
        connectNulls: false,
        itemStyle: { color: palette.realised },
        lineStyle: { color: palette.realised, width: 2.5, type: "solid" },
        areaStyle: { color: palette.realised, opacity: 0.26 },
        emphasis: { focus: "series", scale: 1.5 },
        markArea: missingCoverage
          ? {
              silent: true,
              label: { show: false },
              itemStyle: { color: palette.grid, opacity: 0.22 },
              data: [[{ xAxis: firstInstant }, { xAxis: coverageInstant }]],
            }
          : undefined,
      },
    ],
  };
}

function paletteFromDocument(): ChartPalette {
  const style = getComputedStyle(document.documentElement);
  const token = (name: string): string => style.getPropertyValue(name).trim();
  return {
    background: token("--color-surface-default"),
    border: token("--color-border-strong"),
    grid: token("--color-chart-grid"),
    fontMono: token("--font-mono"),
    fontSans: token("--font-sans"),
    negative: token("--color-chart-negative"),
    positive: token("--color-chart-positive"),
    realised: token("--color-status-info"),
    text: token("--color-text-primary"),
    muted: token("--color-text-muted"),
  };
}

function humanCoverage(at: string | null): React.ReactNode {
  if (at === null) return null;
  const instant = Date.parse(at);
  if (!Number.isFinite(instant)) return null;
  return <time dateTime={at}>{formatAxisMoment(instant)} UTC</time>;
}

export function PnlChart({
  analytics,
  portfolioAvailable,
}: {
  readonly analytics: AccountAnalyticsResponse;
  readonly portfolioAvailable: boolean;
}): React.JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ChartHandle | null>(null);
  const latestRef = useRef<ChartState>({
    analytics,
    portfolioAvailable,
    visible: "both",
  });
  const [visible, setVisible] = useState<VisibleSeries>(
    portfolioAvailable ? "both" : "realised",
  );
  const exact = exactSeries(analytics, portfolioAvailable);
  const empty = exact.portfolio.length === 0 && exact.realised.length === 0;
  const partial = !portfolioAvailable || exact.portfolio.length === 0;
  const active = !portfolioAvailable && visible === "both" ? "realised" : visible;
  latestRef.current = { analytics, portfolioAvailable, visible: active };

  useEffect(() => {
    const host = hostRef.current;
    if (host === null || typeof ResizeObserver === "undefined") return undefined;
    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;
    let themeObserver: MutationObserver | null = null;

    void import("./chartEngine").then(({ initialiseChart }) => {
      if (cancelled) return;
      const chart = initialiseChart(host);
      chartRef.current = chart;
      const current = latestRef.current;
      chart.setOption(
        buildPnlChartOption(
          current.analytics,
          current.portfolioAvailable,
          paletteFromDocument(),
          current.visible,
        ),
        true,
      );
      resizeObserver = new ResizeObserver(() => {
        chart.resize();
      });
      resizeObserver.observe(host);
      themeObserver = new MutationObserver(() => {
        const state = latestRef.current;
        chart.setOption(
          buildPnlChartOption(
            state.analytics,
            state.portfolioAvailable,
            paletteFromDocument(),
            state.visible,
          ),
          true,
        );
      });
      themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-theme"],
      });
    });

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      themeObserver?.disconnect();
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (chart === null) return;
    chart.setOption(
      buildPnlChartOption(analytics, portfolioAvailable, paletteFromDocument(), active),
      true,
    );
  }, [active, analytics, portfolioAvailable]);

  const timestamps = Array.from(
    new Set([...exact.portfolio, ...exact.realised].map((point) => point.at)),
  );

  return (
    <Panel title="Динамика PnL" testId="pnl-chart-panel" variant="analytics">
      <div data-testid="pnl-chart" data-active-series={active}>
        <div
          role="group"
          aria-label="Серии графика"
          className="mb-2 flex flex-wrap gap-1.5"
        >
          {(Object.keys(SERIES) as SeriesId[]).map((id) => {
            const available =
              id === "portfolio" ? exact.portfolio.length > 0 : exact.realised.length > 0;
            const pressed = active === "both" || active === id;
            return (
              <Button
                key={id}
                variant="ghost"
                size="sm"
                selected={pressed}
                aria-pressed={pressed}
                disabled={!available}
                onClick={() => {
                  setVisible((current) => (current === id ? "both" : id));
                }}
                className="min-h-9 gap-2 border border-border-subtle px-2.5 aria-pressed:border-border-strong pointer-coarse:min-h-11"
              >
                <span
                  aria-hidden="true"
                  className={`h-0.5 w-6 ${
                    id === "portfolio"
                      ? "bg-chart-positive"
                      : "border-t-2 border-dashed border-status-info"
                  }`}
                />
                {SERIES[id]}
              </Button>
            );
          })}
        </div>

        {partial && (
          <p
            className="mb-2 flex flex-wrap gap-x-1 border-l-2 border-status-warning pl-2 text-helper-sm text-text-secondary"
            data-testid="analytics-coverage"
          >
            <span>Портфельная кривая доступна не за весь выбранный период.</span>
            {analytics.coverage_since !== null && (
              <span>
                Доступные данные начинаются {humanCoverage(analytics.coverage_since)}.
              </span>
            )}
          </p>
        )}

        {empty ? (
          <p className="py-10 text-center text-body-main text-text-muted">
            За выбранный период точек PnL нет.
          </p>
        ) : (
          <div
            ref={hostRef}
            role="img"
            aria-label={chartLabel()}
            data-testid="pnl-chart-canvas"
            className="h-72 min-h-72 w-full md:h-96 md:min-h-96"
          />
        )}

        <details className="mt-2 border-t border-border-subtle pt-2 text-helper-sm text-text-secondary">
          <summary className="w-fit cursor-pointer select-none rounded-row px-1 py-1 hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring">
            Точные данные
          </summary>
          <div className="mt-2 max-h-64 overflow-y-auto rounded-row border border-border-subtle">
            <table
              data-testid="analytics-chart-table"
              className="w-full border-collapse bg-surface-default text-left"
            >
              <caption className="sr-only">Точные значения кривых PnL в UTC</caption>
              <thead className="sticky top-0 z-10 bg-surface-subtle text-label-caps text-text-secondary">
                <tr>
                  <th className="px-2.5 py-2 font-medium">Момент UTC</th>
                  <th className="px-2.5 py-2 text-right font-medium">
                    Общий PnL портфеля
                  </th>
                  <th className="px-2.5 py-2 text-right font-medium">
                    Накопленный реализованный PnL (NET)
                  </th>
                </tr>
              </thead>
              <tbody className="font-mono text-data text-text-primary">
                {timestamps.map((at) => {
                  const portfolioPoint = exact.portfolio.find((point) => point.at === at);
                  const realisedPoint = exact.realised.find((point) => point.at === at);
                  return (
                    <tr
                      key={at}
                      tabIndex={0}
                      className="border-t border-border-subtle focus-visible:bg-surface-selected focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-focus-ring"
                    >
                      <th className="break-all px-2.5 py-2 font-normal sm:whitespace-nowrap sm:break-normal">{at}</th>
                      <td className="whitespace-nowrap px-2.5 py-2 text-right">
                        {portfolioPoint === undefined ? "—" : portfolioPoint.value}
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-2 text-right">
                        {realisedPoint === undefined ? "—" : realisedPoint.value}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      </div>
    </Panel>
  );
}
